import React, { useEffect, useState } from 'react';
import GrowthShell from '../../components/growth/GrowthShell';
import { fmtAt, riyadhDay } from '../../lib/growthRange';
import {
  adsMonthToDate, adsPace, feeTiersText, fetchContext, fetchLiveValues, removeContext, saveContext,
} from '../../lib/growthDirector';

// Growth OS «وضع معبر» — owner, 2026-11-02. What the owner tells the Growth
// Director about Maabar: business model, services, rules, budget, decisions,
// and plan numbers. Live values (fee tiers, cart minimum, fixed costs, ad
// spend) are read from their source of truth on every load and cannot be
// typed here. Every edit keeps the previous text (growth_director_context_history).

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 }));

function LiveRow({ label, value, source }) {
  return <tr><td>{label}</td><td dir="auto">{value}</td><td className="gos-faint">{source}</td></tr>;
}

function Live({ v }) {
  const mtd = adsMonthToDate(v.ads_mtd);
  const today = riyadhDay();
  const [y, m, d] = today.split('-').map(Number);
  const pace = adsPace(Number(v.ads_plan_sar), mtd.sar, d, new Date(y, m, 0).getDate());
  return (
    <section className="gos-card" data-section="live" style={{ marginBottom: 16 }}>
      <div className="gos-card-head"><h3>القيم الحيّة</h3></div>
      <p className="gos-card-sub">تُقرأ من مصدرها في كل مرة. لتغييرها غيّرها في مصدرها.</p>
      <table className="gos-table" data-live="1">
        <thead><tr><th>القيمة</th><th>الآن</th><th>المصدر</th></tr></thead>
        <tbody>
          <LiveRow label="شرائح رسوم الخدمة (للإدارة فقط — لا تُعرض للتاجر كنِسب)"
                   value={v.fee_tiers_enabled ? feeTiersText(v.fee_tiers) : `ثابتة ${fmt(Number(v.fee_rate) * 100)}% (الشرائح متوقفة)`} source="إعدادات المتجر" />
          <LiveRow label="الحد الأدنى للسلة" value={`${fmt(v.min_cart_sar)} ر.س`} source="إعدادات المتجر" />
          <LiveRow label="السعر الموصول" value={v.delivered_enabled ? 'مفعّل' : 'متوقف'} source="إعدادات المتجر" />
          <LiveRow label="ميزانية خسارة الشحن الشهرية" value={`${fmt(v.loss_monthly_budget_sar)} ر.س`} source="إعدادات المتجر" />
          <LiveRow label="رسم الطلب المُدار (آخر رسم مسجّل)" value={v.request_fee_sar == null ? '—' : `${fmt(v.request_fee_sar)} ر.س`} source="رسوم الطلبات" />
          <LiveRow label="التكاليف الثابتة الشهرية (شامل الضريبة حيث تنطبق)"
                   value={<>{fmt(v.fixed_monthly_sar)} ر.س <span className="gos-faint">({(v.fixed_monthly_items || []).map((i) => `${i.label} ${fmt(i.sar)}`).join(' · ')})</span></>}
                   source="الربحية ← الإعدادات" />
          <LiveRow label={`إنفاق الإعلانات هذا الشهر (${v.ads_month})`}
                   value={<>{fmt(mtd.sar)} ر.س من خطة {fmt(v.ads_plan_sar)}
                     {pace && <span className={pace.over || pace.ahead ? 'gos-pill bad' : 'gos-faint'} style={{ marginInlineStart: 8 }}>
                       {pace.over ? 'تجاوز خطة الشهر' : pace.ahead ? `أسرع من الخطة (المتوقع ${fmt(pace.expected)})` : `المتوقع حتى اليوم ${fmt(pace.expected)}`}</span>}
                     {mtd.unconverted.length > 0 && <span className="gos-faint"> · غير محسوب: {mtd.unconverted.join('، ')}</span>}</>}
                   source="منصات الإعلان" />
        </tbody>
      </table>
    </section>
  );
}

function SectionEditor({ row, onSaved }) {
  const [r, setR] = useState(row);
  const [state, setState] = useState(null);
  const dirty = r.title_ar !== row.title_ar || r.body !== row.body || String(r.value_num ?? '') !== String(row.value_num ?? '');
  const save = async () => {
    setState('…');
    try {
      await saveContext({ ...r, value_num: r.kind === 'plan' ? (r.value_num === '' || r.value_num == null ? null : Number(r.value_num)) : null });
      setState('حُفظ'); onSaved();
    } catch (e) { setState(`تعذّر الحفظ: ${e?.message || e}`); }
  };
  const del = async () => {
    if (!window.confirm(`حذف «${r.title_ar}»؟ (يبقى في السجل)`)) return;
    try { await removeContext(r.key); onSaved(); } catch (e) { setState(`تعذّر الحذف: ${e?.message || e}`); }
  };
  const custom = r.key.startsWith('custom_');
  return (
    <div className="gos-ctx" data-context={r.key}>
      <div className="gos-ctx-head">
        {custom ? <input className="gos-sinput name" value={r.title_ar} onChange={(e) => setR({ ...r, title_ar: e.target.value })} aria-label="العنوان" />
                : <h4 className="gos-subhead" style={{ margin: 0 }}>{r.title_ar}</h4>}
        <span className="gos-faint">آخر تعديل {fmtAt(row.updated_at)}</span>
      </div>
      {r.kind === 'plan' ? (
        <label className="gos-sfield"><input className="gos-sinput" type="number" step="any" min="0" dir="ltr" value={r.value_num ?? ''}
                                             onChange={(e) => setR({ ...r, value_num: e.target.value })} /><span>{r.unit_ar}</span></label>
      ) : (
        <textarea className="gos-textarea" dir="auto" rows={Math.min(14, Math.max(3, (r.body || '').split('\n').length + 1))}
                  value={r.body} onChange={(e) => setR({ ...r, body: e.target.value })} aria-label={r.title_ar} />
      )}
      <div className="gos-ctx-foot">
        <button type="button" className="gos-btn" style={{ flex: '0 0 auto' }} disabled={!dirty} onClick={save}>حفظ</button>
        {custom && <button type="button" className="gos-btn ghost" style={{ flex: '0 0 auto' }} onClick={del}>حذف</button>}
        {state && <span className="gos-faint">{state}</span>}
      </div>
    </div>
  );
}

export default function GrowthContext({ user, profile, lang }) {
  const [rows, setRows] = useState(undefined);
  const [live, setLive] = useState(undefined);
  const load = () => {
    fetchContext().then(setRows).catch((e) => { console.warn('[GrowthContext] context:', e?.message || e); setRows(null); });
    fetchLiveValues().then(setLive).catch((e) => { console.warn('[GrowthContext] live:', e?.message || e); setLive(null); });
  };
  useEffect(() => { load(); }, []);
  const add = async () => {
    const sort = Math.max(0, ...(rows || []).filter((r) => r.kind === 'section').map((r) => r.sort)) + 10;
    try { await saveContext({ key: `custom_${Date.now().toString(36)}`, kind: 'section', title_ar: 'قسم جديد', body: '', sort }); load(); }
    catch (e) { window.alert(`تعذّرت الإضافة: ${e?.message || e}`); }
  };

  return (
    <GrowthShell user={user} profile={profile} lang={lang} active="context" title="وضع معبر">
      <div className="gos-hello">
        <h2>وضع معبر</h2>
        <p>ما يعرفه مدير النمو عن معبر. اكتب هنا نموذج العمل والقواعد والقرارات بكلماتك. كل تعديل يُحفظ مع النسخة السابقة.</p>
      </div>
      {live === null && <div className="gos-card" style={{ marginBottom: 16 }}><div className="gos-empty-state"><strong>تعذّر تحميل القيم الحيّة</strong>أعد تحميل الصفحة.</div></div>}
      {live && <Live v={live} />}
      <section className="gos-card" data-section="context">
        <div className="gos-card-head"><h3>ما تكتبه أنت</h3></div>
        {rows === null && <div className="gos-empty-state"><strong>تعذّر التحميل</strong>أعد تحميل الصفحة.</div>}
        {(rows || []).map((r) => <SectionEditor key={`${r.key}:${r.updated_at}`} row={r} onSaved={load} />)}
        {rows && <button type="button" className="gos-btn ghost" style={{ flex: '0 0 auto', marginTop: 12 }} onClick={add}>+ قسم</button>}
      </section>
    </GrowthShell>
  );
}
